import { v4 as uuid } from "uuid";
import { HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class TimelineCategory extends BaseCategory {
    get name() {
        return "Timeline";
    }

    get type() {
        return SlideCategories.TIMELINE;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for creating a timeline.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Provide milestones for a timeline.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            milestones: [{
                date: "optional string representing the date or time period of the milestone",
                title: "string representing the title of the milestone",
                description: "string providing very short details about the milestone",
                icon: "optional string keyword for an icon if the slide content suggests one",
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.milestones)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildTimeline(model, options),
            this.buildJourney(model, options)
        ];
        return models;
    }

    buildTimeline(model, options = {}) {
        let builder = new SlideModelBuilder("timeline", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const connectors = [];
        const items = [];

        let x = 0.1;
        const xDelta = 0.8 / model.milestones.length;

        for (let i = 0; i < model.milestones.length; i++) {
            const y = (i % 2 == 0) ? 0.25 : 0.65;

            const item = {
                id: uuid(),
                nodeType: model.milestones[i].icon ? "content-and-text" : "bullet-text",
                text: {
                    blocks: [
                        builder.createBlock(model.milestones[i].date, TextStyleType.LABEL),
                        builder.createBlock(model.milestones[i].title, TextStyleType.TITLE),
                        builder.createBlock(model.milestones[i].description, TextStyleType.BODY),
                    ].filter(b => b),
                },
                textDirection: "right",
                hasBeenDragged: false,
                ...builder.createIcon(model.milestones[i].icon),
                x, y
            };
            items.push(item);

            const connector = {
                id: "from-" + item.id,
                source: item.id,
                target: "/primary/timeline",
                endPointIsLocked: true,
                startAnchor: "anchor-left",
                endAnchor: "anchor-free",
                connectorType: "straight",
                canChangeConnectorType: false,
                startDecoration: "none",
                lineStyle: "solid",
                lineWeight: 2,
                color: "theme"
            };
            connectors.push(connector);

            x += xDelta;
        }

        builder.createPrimary({
            showStartMarker: false,
            showEndMarker: false,
            annotations: { items, connectors },
        });
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }

    buildJourney(model, options = {}) {
        let builder = new SlideModelBuilder("journey", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        builder.createPrimary({
            showStartMarker: false,
            showEndMarker: false,
            milestoneStyle: model.milestones.some(m => m.icon) ? "with-icons" : "dots",
            items: model.milestones.map(milestone => ({
                ...builder.createIcon(milestone.icon),
                text: {
                    blocks: [
                        builder.createBlock([milestone.date, milestone.title].filter(Boolean).join(": "), TextStyleType.TITLE),
                        builder.createBlock(milestone.description, TextStyleType.BODY),
                    ],
                },
            })),
        });
        // if (model.conclusion) {
        //     builder.createBottomTray(model.conclusion);
        // }
        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }
        return builder.model;
    }
}

